import {
  IcbtTranslationProvider,
  ProgramPages,
  SelectIcbt,
  type Language,
} from "@mindlercare/icbt";
import { useFlag } from "@mindlercare/unleash-client";
import { Spinner } from "@mindlercare/ui-web";
import { isForbiddenError } from "api/helpers";
import { mapTreatmentData } from "components/icbt/assignIcbtModal/common";
import { useIcbtLanguage } from "components/icbt/hooks/useTreatment";
import { useIcbtTranslations } from "components/icbt/hooks/useIcbtTranslations";
import { Questionnaire } from "components/icbt/programs/ContentfulRichText/Questionnaire";
import { PreviewProgramModal } from "components/patients/patientCard/icbt/modals/PreviewProgramModal";
import { Typography } from "components/typography";
import { useSelectedMarket } from "core/selectors/marketSelectors";
import { useQueryErrorLogger } from "hooks";
import { useGetAllProgramsAndTreatmentsQuery } from "hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { useGetProgramContentQuery } from "hooks/icbt/useGetProgramContentQuery";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";

export const IcbtPreviewPage = () => {
  const { t } = useTranslation();
  const icbtLanguage = useIcbtLanguage();
  const [selectedLanguage, setSelectedLanguage] =
    useState<Language>(icbtLanguage);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<
    string | undefined
  >(undefined);
  const [previewProgramId, setPreviewProgramId] = useState<string | undefined>(
    undefined,
  );
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const enableAudio = useFlag("APP_ICBT_SOUND");
  const enableVideo = useFlag("APP_ICBT_VIDEO");
  const market = useSelectedMarket();
  const translations = useIcbtTranslations();

  const {
    data: programsAndTreatments,
    isLoading,
    error: programsAndTreatmentsError,
  } = useGetAllProgramsAndTreatmentsQuery(selectedLanguage?.codeISO2);

  const isError = useQueryErrorLogger(
    {
      programsAndTreatmentsError,
      icbtRequestsError: null,
      allProgressError: null,
    },
    "Failed to load data in ICBT preview page",
    { team: "dna", tag: "assignIcbt" },
  );

  const treatments = useMemo(
    () => mapTreatmentData(programsAndTreatments, undefined, undefined, t),
    [programsAndTreatments, t],
  );

  const introTreatmentId = useMemo(
    () => treatments.sorted.find((tr) => tr.introduction)?.id,
    [treatments],
  );

  const effectiveTreatmentId = selectedTreatmentId ?? introTreatmentId;

  const initialPreviewProgramId = useMemo(() => {
    if (!effectiveTreatmentId) return undefined;
    return treatments.byId[effectiveTreatmentId]?.programs[0]?.id;
  }, [effectiveTreatmentId, treatments]);

  const effectivePreviewProgramId = previewProgramId ?? initialPreviewProgramId;

  const {
    data: programContent,
    isFetching: programContentFetching,
    error: programContentError,
  } = useGetProgramContentQuery({
    programId: effectivePreviewProgramId,
    languageCode: selectedLanguage?.codeISO2,
  });

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <CenteredContainer>
        <Typography insensitive variant="h3">
          {isForbiddenError(programsAndTreatmentsError)
            ? t("ICBT_LOADING_DATA_ERROR_FORBIDDEN")
            : t("ICBT_LOADING_DATA_ERROR")}
        </Typography>
      </CenteredContainer>
    );
  }

  return (
    <PageContainer>
      <IcbtTranslationProvider translations={translations}>
        <SelectIcbt
          treatments={treatments}
          selectedTreatmentId={effectiveTreatmentId}
          selectedProgramId={effectivePreviewProgramId}
          programsToAssign={{}}
          assigningEnabled={false}
          isPreviewOnly={true}
          closeAssignIcbtModal={() => undefined}
          selectIcbtAssignButtonHandler={() => undefined}
          handleSelectTreatment={(treatmentId) => {
            setSelectedTreatmentId(treatmentId);
            if (treatmentId) {
              const firstProgram = treatments.byId[treatmentId]?.programs[0];
              if (firstProgram) setPreviewProgramId(firstProgram.id);
            }
          }}
          onProgramSelectionChanged={() => undefined}
          onProgramPreviewSelect={setPreviewProgramId}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          languages={market?.languages}
        >
          <SelectIcbt.ProgramPreview
            title={programContent?.title}
            isLoading={programContentFetching}
            error={programContentError}
            onExpandPreview={() => setPreviewModalOpen(true)}
          >
            {programContent?.pages && (
              <ProgramPages
                pages={programContent.pages}
                enableAudio={enableAudio}
                enableVideo={enableVideo}
                renderQuestionnaire={(entry) => (
                  <Questionnaire
                    key={entry.sys.id}
                    questionnaireType="icbt"
                    clinicalId={entry.clinicalId}
                  />
                )}
              />
            )}
          </SelectIcbt.ProgramPreview>
        </SelectIcbt>

        {effectivePreviewProgramId && (
          <PreviewProgramModal
            open={previewModalOpen}
            handleCloseProgramPreviewModal={() => setPreviewModalOpen(false)}
            programId={effectivePreviewProgramId}
            languageCode={selectedLanguage?.codeISO2}
          />
        )}
      </IcbtTranslationProvider>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px 40px 40px 40px;
  background-color: ${({ theme }) => theme.COLORS.WHITE};
`;

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;
